
import axios, {
  AxiosError,
  AxiosResponse,
  AxiosInstance,
  AxiosRequestConfig,
} from 'axios';

import { toast } from 'react-toastify';
import { IAuthenticationProvider, IConfigurationProvider } from '../@types/providers';
import {
  HttpMethod,
  ITecpayApi,
  IApiResponse,
  ITecpayRequest,
  IRequestBuilder,
  HttpStatus,
  ResponseMessage,
} from './types';
import { IObjectMap } from '../../global/types';
import { doLogout } from '../../store/user/actions';
import store from '../../store';
import { refreshSession } from '../auth/sessionRefresh';
// Providers montados direto dos módulos, não por `./factories`: factories importa
// este arquivo, e importar de volta fecharia um ciclo.
import configurationProvider from './configuration';
import storageProvider from './storage';
import authenticationProvider from './authentication';
import { objectUtils } from '../../utils';

// @ts-ignore
let axiosInstanceAux:any;

// @ts-ignore
let axiosInstanceLinkPagamentoAux:any;

/**
 * Instância do axios da API legada.
 *
 * Cria a instância na hora se ela ainda não existir. Antes isto só devolvia o que
 * o `createAxiosInstance` tivesse deixado como efeito colateral da primeira chamada
 * feita via `apiProvider...sendAsync()` — então quem entrasse direto numa tela que
 * só usa `getAxiosInstance()` (F5 no cancelamento, por exemplo) recebia `undefined`
 * e o erro aparecia como "Cannot read properties of undefined (reading 'post')",
 * antes de qualquer requisição sair.
 */
// O retorno segue `any` de propósito: tipá-lo como AxiosInstance revela erros de
// tipo pré-existentes em telas que hoje consomem este retorno sem tipagem, e
// corrigi-los não pertence a esta correção.
export const getAxiosInstance = (): any => {
  if (!axiosInstanceAux) {
    createAxiosInstance(
      true,
      authenticationProvider(storageProvider(), objectUtils()),
      configurationProvider(),
    );
  }

  return axiosInstanceAux;
};

/**
 * Instância do axios do serviço de link de pagamento.
 *
 * A URL sai do `config`, como todas as outras: estava fixa no host de HML, então
 * em produção só o relatório de links saía do ambiente errado enquanto o resto
 * da tela, que usa `config.wlBaseurl`, ia para produção.
 */
export const getAxiosInstanceLinkPagamento = () => {
  if (axiosInstanceLinkPagamentoAux == null) {
    const { wlBaseurl } = configurationProvider().getConfig();
    const axiosInstanceLinkPagamento = axios.create({ baseURL: wlBaseurl });
    axiosInstanceLinkPagamento.interceptors.request.use(
      (config) => {
        // O serviço de link valida o JWT da API antiga, não o do VAULT: o
        // `accessToken` passou a ser o do VAULT e chega aqui como 401. A queda para
        // ele mantém de pé a sessão aberta antes da virada do login, onde ainda é o
        // próprio token legado — é a mesma regra do interceptor da API legada abaixo.
        const authProvider = authenticationProvider(storageProvider(), objectUtils());
        const token = authProvider.getLegacyAuthenticationToken()
          ?? authProvider.getAuthenticationToken();

        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }

        const url = config.url || "";
        const empresaSelecionada = JSON.parse(localStorage.getItem('selectedCompany') as string);

        if (url.includes("downloadRelatorioLinks")) {
          config.responseType = "blob";
        } else if (empresaSelecionada?.id) {        
          config.params = {
            ...config.params,
            businessId: empresaSelecionada.id,
          };
        }

        return config;
      },
      (error) => {
        // Do something with request error
        return Promise.reject(error);
      }
    );
    axiosInstanceLinkPagamento.interceptors.response.use((response) => response);
    axiosInstanceLinkPagamentoAux = axiosInstanceLinkPagamento;
  }

  return axiosInstanceLinkPagamentoAux;
}



type RetriableRequestConfig = AxiosRequestConfig & { retriedAfterRefresh?: boolean };

export const createAxiosInstance = (
  hasAuthentication: boolean,
  authProvider: IAuthenticationProvider,
  configProvider: IConfigurationProvider,
): AxiosInstance => {
  const { apiBaseUrl } = configProvider.getConfig();
  const axiosInstance = axios.create({ baseURL: apiBaseUrl });

  // const handleBadRequestMessages = (errorResponse: AxiosResponse<any>): void => {
  //   const parsedResponse = errorResponse as AxiosResponse<IApiResponse<IApiErrorDto[]>>;
  //   const { data } = errorResponse;
  //   if (!data || !(data.response instanceof Array) || parsedResponse.data.response.length === 0) {
  //     toast.error(data.message);
  //     return;
  //   }
  //
  //   parsedResponse.data.response.forEach((error) => {
  //     toast.error(error.message);
  //   });
  // };

  // Renovação real do token (C4). Antes isto devolvia true sem fazer nada, e a
  // sessão morria com o access token — 5 minutos, no VAULT.
  const handleTokenRefresh = (): Promise<boolean> => refreshSession();

  const requestInterceptorHandler = (config: AxiosRequestConfig) => {
    if (!hasAuthentication) {
      return config;
    }

    const token = authProvider.getAuthenticationToken();
    return {
      ...config,
      headers: {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const erroResponseInterceptorHandler = async (error: AxiosError) => {
    const newError = { ...error } as AxiosError;
    const errorResponse = newError.response as AxiosResponse<IApiResponse<Record<string, unknown>>>;

    if (!errorResponse) {
      return ({ status: 500 });
    }

    // if (errorResponse.status === HttpStatus.BAD_REQUEST) {
    //   handleBadRequestMessages(errorResponse);
    // }

    if (errorResponse.status === HttpStatus.UNAUTHORIZED) {
      const original = error.config as RetriableRequestConfig | undefined;
      const refreshed = await handleTokenRefresh();

      // Uma repetição só: se a chamada com o token novo também voltar 401, o
      // problema não é o token, e insistir vira laço.
      if (refreshed && original && !original.retriedAfterRefresh) {
        original.retriedAfterRefresh = true;
        return axiosInstance(original);
      }

      if (!refreshed || errorResponse.data.message === ResponseMessage.INVALID_CREDENTIALS) {
        doLogout()(store.dispatch)
        toast.dismiss();
      }
    }

    return ({ ...errorResponse });
  };

  axiosInstance.interceptors.request.use(
    (config) => {
      // Estas chamadas vão para a API antiga, que valida o JWT dela — o token do
      // VAULT é recusado lá. Vai o token legado, emitido no login pelo BFF.
      //
      // A queda para o `accessToken` é o que mantém de pé a sessão aberta antes da
      // virada, onde ele ainda é o próprio token legado.
      const token = authProvider.getLegacyAuthenticationToken()
        ?? authProvider.getAuthenticationToken();

      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      const empresaSelecionada = JSON.parse(localStorage.getItem('selectedCompany') as  string);

      if (empresaSelecionada) {
        config.params = {
          ...config.params,
          businessId: empresaSelecionada.id,
          merchantId: empresaSelecionada.merchantId
        };
      }

      return config;
    },
    (error) => {
      // Do something with request error
      return Promise.reject(error);
    }
  );
  axiosInstance.interceptors.response.use((response) => response, erroResponseInterceptorHandler);
  axiosInstanceAux = axiosInstance;
  return axiosInstance;
};

export default (
  authProvider: IAuthenticationProvider,
  configProvider: IConfigurationProvider,
): ITecpayApi => {
  const requestHandlerFactory = (method: HttpMethod, hasAuthentication: boolean) => {
    const axiosInstance = createAxiosInstance(hasAuthentication, authProvider, configProvider);
    switch (method) {
      case HttpMethod.DELETE:
        return axiosInstance.delete;
      case HttpMethod.PUT:
        return axiosInstance.put;
      case HttpMethod.POST:
        return axiosInstance.post;
      case HttpMethod.GET:
        return axiosInstance.get;
      case HttpMethod.PATCH:
        return axiosInstance.patch;
      default:
        throw new Error('Invalid HttpRequest method');
    }
  };

  const createRequest = <T, TU>(params: ITecpayRequest<T>): IRequestBuilder<T, TU> => {
    const {
      url,
      data,
      search = {},
      parameters = {},
      headers = new Map<string, string>(),
      method = HttpMethod.GET,
      hasAuthentication = true,
    } = params;

    const composeRequestConfig = (): AxiosRequestConfig => {
      if (method === null || !(method in HttpMethod)) {
        throw new Error('Request must have a Http method!');
      }

      return ({
        data,
        params: search,
        headers: { ...headers },
      });
    };

    const addHeader = (name: string, value: string): IRequestBuilder<T, TU> => createRequest({
      ...params,
      headers: {
        ...headers,
        [name]: value,
      },
    });

    const withSearch = (searchObj: IObjectMap): IRequestBuilder<T, TU> => createRequest({
      ...params,
      search: { ...searchObj },
    });

    const withToken = (schema: string, accessToken: string): IRequestBuilder<T, TU> => {
      if (!schema || !accessToken) {
        throw new Error('Missing schema or access token');
      }

      return createRequest<T, TU>({
        ...params,
      }).addHeader('Authorization', `${schema} ${accessToken}`);
    };

    const withData = (requestData: T): IRequestBuilder<T, TU> => createRequest({
      ...params,
      data: requestData,
    });

    const useAuthentication = (value?: boolean): IRequestBuilder<T, TU> => createRequest({
      ...params,
      hasAuthentication: value ?? true,
    });

    const withMethod = (httpMethod: HttpMethod): IRequestBuilder<T, TU> => createRequest({
      ...params,
      method: httpMethod,
    });

    const withParameters = (newParams: IObjectMap): IRequestBuilder<T, TU> => createRequest({
      ...params,
      parameters: { ...newParams },
    });

    const mapUrl = (currentUrl: string, objectParams: IObjectMap): string => {
      const objectKeys = Object.keys(objectParams);
      return objectKeys.reduce((acc: string, currentValue: string) => {
        const key = `:${currentValue}`;
        const value = objectParams[currentValue];
        return acc.replace(key, value);
      }, currentUrl);
    };

    const sendAsync = async (): Promise<IApiResponse<TU>> => {
      const requestConfig = composeRequestConfig();
      const requestHandler = requestHandlerFactory(method, hasAuthentication);
      const newUrl = mapUrl(url, parameters);
      const response = await requestHandler(newUrl, { ...requestConfig.data, params: { ...search } }, { ...requestConfig });
      const responseData = response.data as IApiResponse<TU>;

      return ({ ...responseData });
    };

    return ({
      ...params,
      addHeader,
      withData,
      withToken,
      withMethod,
      withParameters,
      withSearch,
      sendAsync,
      useAuthentication,
    });
  }

  return ({
    createRequest,
  });
};
